import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { MediaTrack } from '@/contexts/media-player-context';
import { trackAudio, workAudioUrl } from '@/lib/track-audio';

/**
 * What a card's Play button plays, shared by Home and the Dashboard (Gordon
 * 2026-11-01: the Dashboard's Recent Releases had no play button "like on
 * home page"). Play never opens the page — it plays the content, or plays
 * nothing (Gordon 2026-10-31).
 *
 * - An item with its own audio (track, demo): that track.
 * - A release: every track with a playable URL, in track order (up to 40). A
 *   track plays its work's own file; the release list's copy only fills in
 *   when the work has none (it drifted once — see lib/track-audio).
 * - A release with nothing playable: the work with the same id, if it has
 *   audio (singles uploaded straight to `works`).
 * - Otherwise: nothing (the caller says so).
 */
export async function playableTracksFor(item: any): Promise<MediaTrack[]> {
  const direct = item.audioUrl || item.mp3Url;
  if (direct) {
    return [{
      id: item.id || 'unknown',
      title: item.title || item.name || 'Track',
      artist: item.artist?.name || item.artist || item.artistName || '',
      imageUrl: item.imageUrl || item.coverArtUrl || '',
      audioUrl: direct,
      mediaType: 'audio',
      genre: item.genre,
    }];
  }
  const snap = await getDoc(doc(db, 'releases', item.id));
  const rel = snap.exists() ? (snap.data() as any) : null;
  const relTracks: any[] = (rel?.tracks || []).slice()
    .sort((a: any, b: any) => (a.trackNumber ?? 0) - (b.trackNumber ?? 0))
    .slice(0, 40);
  const resolved = await Promise.all(relTracks.map(async (t: any) => {
    // The recording's own file wins over the list's copy, which can drift (`trackAudio`).
    let url = t.mp3Url || t.audioUrl || '';
    if (t.workId || (!url && t.id)) {
      try {
        const ws = await getDoc(doc(db, 'works', t.workId || t.id));
        url = trackAudio(url, workAudioUrl(ws.exists() ? ws.data() : null));
      } catch { /* not readable — keep the list's copy */ }
    }
    return url ? {
      id: t.workId || t.id || `${item.id}-${t.trackNumber}`,
      title: t.title || item.title || 'Track',
      artist: t.artistName || rel?.artistName || rel?.artist?.name || item.artist || '',
      imageUrl: rel?.coverArtUrl || item.imageUrl || item.coverArtUrl || '',
      audioUrl: url,
      mediaType: 'audio' as const,
      genre: rel?.genre || item.genre,
    } : null;
  }));
  const tracks = resolved.filter(Boolean) as MediaTrack[];
  if (tracks.length > 0) return tracks;

  const wsnap = await getDoc(doc(db, 'works', item.id));
  const w = wsnap.exists() ? (wsnap.data() as any) : null;
  if (w?.mp3Url || w?.audioUrl) {
    return [{
      id: item.id,
      title: w.title || item.title || 'Track',
      artist: w.artist?.name || w.artistName || item.artist || '',
      imageUrl: w.coverArtUrl || item.imageUrl || item.coverArtUrl || '',
      audioUrl: w.mp3Url || w.audioUrl,
      mediaType: 'audio',
      genre: w.genre || item.genre,
    }];
  }
  return [];
}
